<template>
  <section v-if="pending.length" class="chat-moderation-pending" aria-live="polite">
    <strong>{{ labels.moderation_pending }}</strong>
    <ul><li v-for="message in pending" :key="message.id">{{ message.body }}</li></ul>
  </section>
  <Teleport to="body">
    <dialog ref="dialog" class="chat-moderation-dialog" aria-labelledby="chat-moderation-title" @cancel.prevent>
      <h2 id="chat-moderation-title">{{ labels.moderation_enabled }}</h2>
      <p>{{ labels.moderation_prompt }}</p>
      <p v-if="error" role="alert">{{ error }}</p>
      <div>
        <button type="button" class="chat-moderation-dialog__disable" :disabled="saving" @click="disableModeration">{{ labels.moderation_yes }}</button>
        <button type="button" class="chat-moderation-dialog__keep" :disabled="saving" @click="keepModeration">{{ labels.moderation_no }}</button>
      </div>
    </dialog>
  </Teleport>
</template>

<script setup>
import {nextTick, onBeforeUnmount, onMounted, ref} from 'vue';

const props = defineProps({apiUrl: {type: String, required: true}, csrfToken: {type: String, required: true}, labels: {type: Object, required: true}, promptEnabled: {type: Boolean, default: true}});
const emit = defineEmits(['resolved', 'state-loaded', 'changed']);
const pending = ref([]);
const dialog = ref(null);
const saving = ref(false);
const error = ref('');
let signature = null;
let timer = null;
let request = null;
let disposed = false;
let promptHandled = false;

const refresh = async () => {
  if (disposed || request || saving.value) return;
  try {
    request = $.ajax({url: props.apiUrl, method: 'GET', dataType: 'json', cache: false});
    const response = await request;
    if (disposed) return;
    if (response?.status !== 'OK') throw new Error('Invalid moderation response');
    const nextPending = response.result.pending ?? [];
    const nextSignature = JSON.stringify(nextPending.map((message) => message.id));
    const shouldPrompt = response.result.prompt && props.promptEnabled && !promptHandled;
    if (signature !== null && signature !== nextSignature) emit('resolved');
    signature = nextSignature;
    pending.value = nextPending;
    emit('state-loaded', response.result);
    await nextTick();
    if (shouldPrompt && !dialog.value?.open) {
      promptHandled = true;
      dialog.value?.showModal();
    }
    else if (!response.result.prompt && dialog.value?.open) dialog.value.close();
  } catch (failure) {
    if (!disposed && dialog.value?.open) error.value = props.labels.error;
  } finally {
    request = null;
  }
};

const disableModeration = async () => {
  if (saving.value) return;
  saving.value = true;
  error.value = '';
  try {
    const response = await $.ajax({url: props.apiUrl, method: 'PUT', contentType: 'application/json', dataType: 'json', data: JSON.stringify({enabled: false, _token: props.csrfToken})});
    if (response?.status !== 'OK') throw new Error('Invalid moderation answer');
    emit('changed', false);
    dialog.value?.close();
  } catch (failure) {
    error.value = failure?.responseJSON?.message ?? props.labels.error;
  } finally {
    saving.value = false;
  }
};
const keepModeration = () => { dialog.value?.close(); };

onMounted(() => {
  refresh();
  timer = window.setInterval(() => { if (!document.hidden) refresh(); }, 30000);
});
onBeforeUnmount(() => {
  disposed = true;
  window.clearInterval(timer);
  request?.abort?.();
  dialog.value?.close();
});
defineExpose({refresh});
</script>

<style scoped>
.chat-moderation-pending { padding: 12px 16px; background: #fff6df; max-height: 150px; overflow: auto; flex-shrink: 0; }
.chat-moderation-pending ul { margin: 8px 0 0; padding-left: 20px; }
.chat-moderation-pending li { white-space: pre-wrap; overflow-wrap: anywhere; }
.chat-moderation-dialog { border: 0; border-radius: 16px; padding: 28px; max-width: 440px; width: calc(100% - 32px); color: #34323b; }
.chat-moderation-dialog::backdrop { background: #0009; }
.chat-moderation-dialog h2 { font-size: 20px; margin-bottom: 16px; }
.chat-moderation-dialog p { line-height: 1.8; }
.chat-moderation-dialog > div { display: flex; gap: 12px; margin-top: 24px; }
.chat-moderation-dialog button { flex: 1; padding: 12px; border: 1px solid transparent; border-radius: 8px; color: #fff; }
.chat-moderation-dialog__disable { background: #f28b22; }
.chat-moderation-dialog__keep { background: #222; }
.chat-moderation-dialog button:disabled { opacity: .5; }
</style>
